/* Inquiry form: progressive enhancement of the plugin's markup (architecture §7).
 * Client validation mirrors the server rules; JSON POST to the REST route; success only on 201. */

const i18n = window.mkI18n || {};

function initForm(form) {
  const endpoint = form.dataset.mkEndpoint;
  if (!endpoint || !window.fetch) return;

  const submit = form.querySelector('[type="submit"]');
  const status = form.querySelector('[data-mk-status]');
  const fields = ['name', 'company', 'email', 'phone', 'service', 'message'];
  const input = (name) => form.elements[name];
  const errorBox = (name) => form.querySelector(`[data-mk-error="${name}"]`);
  const ALERT_ICON = '<svg class="mk-icon mk-icon--alert" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="butt" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5"/><path d="M12 16v.5" stroke-width="2.25"/></svg>';

  const setError = (name, message) => {
    const box = errorBox(name);
    const el = input(name);
    if (box) {
      if (message) {
        box.innerHTML = ALERT_ICON + '<span></span>';
        box.lastChild.textContent = message;
        box.hidden = false;
      } else {
        box.textContent = '';
        box.hidden = true;
      }
    }
    if (el) {
      if (message) el.setAttribute('aria-invalid', 'true');
      else el.removeAttribute('aria-invalid');
    }
  };

  const clearFormError = () => {
    form.querySelectorAll('.mk-form__error--form').forEach((el) => el.remove());
  };

  const showFormError = (message) => {
    clearFormError();
    const p = document.createElement('p');
    p.className = 'mk-form__error mk-form__error--form';
    p.setAttribute('role', 'alert');
    p.innerHTML = ALERT_ICON + '<span></span>';
    p.lastChild.textContent = message;
    const grid = form.querySelector('.mk-form__grid');
    (grid ? grid.parentNode : form).insertBefore(p, grid || form.firstChild);
    if (status) status.textContent = message;
  };

  const validate = () => {
    const errors = {};
    const val = (n) => (input(n) ? String(input(n).value).trim() : '');
    if (!val('name')) errors.name = i18n.nameRequired || 'Please enter your name';
    const email = val('email');
    const phone = val('phone');
    const emailOk = !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const digits = phone.replace(/\D+/g, '');
    const phoneOk = !phone || (digits.length >= 7 && digits.length <= 15 && /^[0-9+\-\s().]+$/.test(phone));
    if ((!email && !phone) || !emailOk) errors.email = i18n.contactInvalid || 'Please enter a valid email or phone number';
    if (phone && !phoneOk) errors.phone = i18n.contactInvalid || 'Please enter a valid email or phone number';
    const message = val('message');
    if (message.length < 20 || message.length > 3000) errors.message = i18n.messageShort || 'Please describe the workflow in at least 20 characters';
    return errors;
  };

  const applyErrors = (errors) => {
    fields.forEach((f) => setError(f, errors[f] || ''));
    const first = fields.find((f) => errors[f]);
    if (first && input(first)) input(first).focus();
  };

  fields.forEach((f) => {
    const el = input(f);
    if (el) el.addEventListener('input', () => setError(f, ''));
  });

  const setLoading = (on) => {
    if (!submit) return;
    const label = submit.querySelector('.mk-btn__label') || submit;
    if (on) {
      submit.dataset.mkLabel = label.textContent;
      label.textContent = i18n.sending || 'Sending…';
      submit.classList.add('is-loading');
      submit.setAttribute('aria-disabled', 'true');
    } else {
      if (submit.dataset.mkLabel) label.textContent = submit.dataset.mkLabel;
      submit.classList.remove('is-loading');
      submit.removeAttribute('aria-disabled');
    }
  };

  const showSuccess = (body) => {
    const wrap = document.createElement('div');
    wrap.className = 'mk-form__success';
    wrap.setAttribute('role', 'status');
    wrap.tabIndex = -1;
    const title = document.createElement('h3');
    title.className = 'mk-form__success-title';
    title.textContent = body.title || form.dataset.mkSuccessTitle || i18n.thankYou || 'Thank you';
    const text = document.createElement('p');
    text.textContent = body.message || form.dataset.mkSuccessBody || i18n.saved || 'Your inquiry was saved.';
    const again = document.createElement('p');
    const link = document.createElement('a');
    link.className = 'mk-btn mk-btn--tertiary';
    link.href = window.location.pathname + '#inquiry';
    link.textContent = i18n.sendAnother || 'Send another inquiry';
    link.addEventListener('click', (e) => {
      e.preventDefault();
      window.location.reload();
    });
    again.appendChild(link);
    wrap.append(title, text, again);
    form.classList.add('is-sent');
    form.prepend(wrap);
    wrap.focus();
  };

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (submit && submit.getAttribute('aria-disabled') === 'true') return;
    clearFormError();
    const errors = validate();
    if (Object.keys(errors).length) {
      applyErrors(errors);
      if (status) status.textContent = i18n.invalid || 'Please check the highlighted fields.';
      return;
    }
    if (status) status.textContent = '';
    setLoading(true);

    const payload = {};
    ['name', 'company', 'email', 'phone', 'service', 'message', 'mk_token', 'website_url', 'lang', 'page'].forEach((k) => {
      payload[k] = input(k) ? String(input(k).value) : '';
    });

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });
      let body = {};
      try {
        body = await res.json();
      } catch (_) {
        body = {};
      }
      if (res.status === 201) {
        setLoading(false);
        showSuccess(body || {});
        return;
      }
      setLoading(false);
      const errs = (body && body.errors) || {};
      applyErrors(errs);
      const formMessage = errs.form || (Object.keys(errs).length ? i18n.invalid : body.message) || i18n.network;
      showFormError(formMessage);
    } catch (_) {
      setLoading(false);
      showFormError(i18n.network || 'Something went wrong. Please try again or email me directly.');
    }
  });
}

document.querySelectorAll('form[data-mk-inquiry]').forEach(initForm);
